function parseMeridiemSuffix(value) {
  const normalized = String(value || '')
    .replace(/[\u00A0\u202F]/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, ' ');

  if (normalized === 'am' || normalized === 'a m') {
    return 'am';
  }

  if (normalized === 'pm' || normalized === 'p m') {
    return 'pm';
  }

  return '';
}

function normalizeFormattedMeridiem(value) {
  return String(value || '')
    .replace(/[\u00A0\u202F]/g, ' ')
    .replace(/a\.\s*m\./gi, 'AM')
    .replace(/p\.\s*m\./gi, 'PM')
    .replace(/\bam\b/g, 'AM')
    .replace(/\bpm\b/g, 'PM')
    .replace(/\s+/g, ' ')
    .trim();
}

export function parseDateValue(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const normalized = String(value)
    .replace(/[\u00A0\u202F]/g, ' ')
    .trim();
  if (!normalized) {
    return null;
  }

  const directDate = new Date(normalized);
  if (!Number.isNaN(directDate.getTime())) {
    return directDate;
  }

  const localizedMatch = normalized.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s+([ap]\.?\s*m\.?))?)?$/i
  );

  if (!localizedMatch) {
    return null;
  }

  const [, day, month, year, hours = '0', minutes = '0', seconds = '0', meridiem = ''] = localizedMatch;
  let normalizedHours = Number(hours);
  const meridiemSuffix = parseMeridiemSuffix(meridiem);

  if (meridiemSuffix === 'pm' && normalizedHours < 12) {
    normalizedHours += 12;
  }

  if (meridiemSuffix === 'am' && normalizedHours === 12) {
    normalizedHours = 0;
  }

  const parsedDate = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    normalizedHours,
    Number(minutes),
    Number(seconds)
  );

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

export function formatHumanDateTime(value, fallback = '-') {
  const parsedDate = parseDateValue(value);

  if (!parsedDate) {
    return fallback;
  }

  try {
    return normalizeFormattedMeridiem(new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(parsedDate));
  } catch {
    return parsedDate.toLocaleString();
  }
}

export function getDateSortValue(value) {
  const parsedDate = parseDateValue(value);
  return parsedDate ? parsedDate.getTime() : Number.NaN;
}

export function compareDateValues(leftValue, rightValue) {
  const leftTimestamp = getDateSortValue(leftValue);
  const rightTimestamp = getDateSortValue(rightValue);
  const leftIsValid = !Number.isNaN(leftTimestamp);
  const rightIsValid = !Number.isNaN(rightTimestamp);

  if (leftIsValid && rightIsValid) {
    return leftTimestamp - rightTimestamp;
  }

  if (leftIsValid) {
    return 1;
  }

  if (rightIsValid) {
    return -1;
  }

  return String(leftValue || '').localeCompare(String(rightValue || ''), 'es', { sensitivity: 'base' });
}

export function isDateLikeColumnLabel(label) {
  const normalized = String(label || '')
    .trim()
    .toLowerCase();

  if (!normalized) {
    return false;
  }

  // These columns already contain a formatted date (including the time zone).
  if (normalized.endsWith(' legible')) {
    return false;
  }

  return (
    normalized.includes('fecha')
    || normalized === 'creadoen'
    || normalized === 'actualizadoen'
    || normalized === 'expiraen'
    || normalized.endsWith('at')
  );
}

export function formatDateValueForLabel(label, value, fallback = '-') {
  if (!isDateLikeColumnLabel(label)) {
    const rawValue = String(value ?? '').trim();
    return rawValue || fallback;
  }

  return formatHumanDateTime(value, fallback);
}

export function formatDateTimesInText(text) {
  const source = String(text || '');
  if (!source.trim()) {
    return source;
  }

  return source.replace(
    /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?|\d{1,2}\/\d{1,2}\/\d{4}(?:,?\s+\d{1,2}:\d{2}(?::\d{2})?(?:\s+[ap]\.?\s*m\.?)?)?)/gi,
    (match) => {
      const formatted = formatHumanDateTime(match, match);
      return formatted || match;
    }
  );
}

export function formatDateTimeInputValue(value) {
  const parsedDate = parseDateValue(value);

  if (!parsedDate) {
    return '';
  }

  const pad = (number) => String(number).padStart(2, '0');

  return `${parsedDate.getFullYear()}-${pad(parsedDate.getMonth() + 1)}-${pad(parsedDate.getDate())}T${pad(parsedDate.getHours())}:${pad(parsedDate.getMinutes())}`;
}

export function getDefaultFutureDateTimeInput(daysAhead = 7) {
  const nextDate = new Date();
  nextDate.setSeconds(0, 0);
  nextDate.setMinutes(0);
  nextDate.setHours(nextDate.getHours() + 1);
  nextDate.setDate(nextDate.getDate() + daysAhead);

  return formatDateTimeInputValue(nextDate);
}
